// Libs
import classNames from 'classnames/bind';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { Box, Button, Grid2, IconButton, Stack, Typography } from '@mui/material';
// Components, Layouts, Pages
import { BaseDatePicker, BaseLabel, BaseTable, Loading, UnderDevelopment } from '~/components/common';
// Others
import { formatDate } from '~/utils/helper/common';
import { IQuarantine } from '~/utils/interface/quarantine';
import { useAppDispatch } from '~/utils/redux/hooks';
import { DateFormatEnum, ReportTypeEnum } from '~/utils/enum/common';
import { IHeaderCellType } from '~/utils/interface/common';
import { getListQuarantine } from '~/thunks/quarantine/quarantineThunk';
import { IPaginationResponse } from '~/utils/interface/base';
import { IQueryParamListExport } from '~/utils/interface/reports';
import { BACK_TO_PREVIOUS_PAGE, DEFAULT_CURRENT_PAGE, DEFAULT_LIMIT_PAGE } from '~/utils/constants/common';
import { exportDataReport } from '~/thunks/reports/reportsThunk';
// Styles, images, icons
import styles from './QuarantineReport.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const columns = (t: TFunction): IHeaderCellType<IQuarantine>[] => {
  return [
    {
      id: 'name',
      label: t('report_table_product_name'),
      render: (record: IQuarantine) => {
        return record?.inventoryName;
      },
    },
    {
      id: 'repairedQuantity',
      label: t('report_table_quantity_repaired'),
      render: (record: IQuarantine) => {
        return record?.repairedQuantity || 0;
      },
    },
    {
      id: 'brokenQuantity',
      label: t('report_table_quantity_cancelled'),
      render: (record: IQuarantine) => {
        return record?.brokenQuantity || 0;
      },
    },
    {
      id: 'startDate',
      label: t('report_table_date_of_damage'),
      render: (record: IQuarantine) => {
        return formatDate(record?.startDate, DateFormatEnum.M_D_YYYY);
      },
    },
  ];
};

const QuarantineReport = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/reports', 'common/base']);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
  const pageSelected = useMemo<number>(() => Number(params?.page ?? DEFAULT_CURRENT_PAGE), [params?.page]);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [quarantineList, setQuarantineList] = useState<IQuarantine[]>([]);
  const [pagination, setPagination] = useState<IPaginationResponse>();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [paramsObject, setParamsObject] = useState<IQueryParamListExport>({
    page: DEFAULT_CURRENT_PAGE,
    limit: DEFAULT_LIMIT_PAGE,
  });
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);

  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetListQuarantine();
  }, [paramsObject.page]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListQuarantine = () => {
    setIsLoading(true);

    dispatch(getListQuarantine(paramsObject))
      .unwrap()
      .then((res) => {
        if (!res?.data) return;

        const { responses, pagination } = res.data;

        setQuarantineList(responses);
        setPagination(pagination);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  };

  const handleApplyFilter = () => {
    handleGetListQuarantine();
  };

  const handleExport = () => {
    setIsLoading(true);

    const params: IQueryParamListExport = {
      ...paramsObject,
      type: ReportTypeEnum.QUARANTINE,
    };

    dispatch(exportDataReport(params))
      .unwrap()
      .then((res) => {
        const { data } = res;

        handleDownloadFile(data?.url);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  };

  const handleDownloadFile = (fileUrl: string) => {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileUrl;
    link.click();
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setParamsObject({ ...paramsObject, page });
    setSearchParams({ ...params, page: String(page) });
  };

  const handleBack = () => {
    navigate(BACK_TO_PREVIOUS_PAGE);
  };

  const handleShowUnderDevelopment = () => {
    setIsShowUnderDevelopment(!isShowUnderDevelopment);
  };
  //#endregion Handle Function

  return (
    <Stack id='quarantineReportPage' className={cx('container')} spacing={6}>
      <Stack direction='row' spacing={2} alignItems='center'>
        <IconButton color='primary' onClick={handleBack} className={cx('buttonBack')}>
          <img src={icons.commonIconBack} alt={t('common_alt_img', { ns: 'common/base' })} />
        </IconButton>

        <Stack direction={'column'} spacing={2}>
          <BaseLabel label={t('reports_quarantine_title')} styleType='bigTitle' />
          <Typography className={cx('description')}>{t('reports_client_income_desc_page')}</Typography>
        </Stack>
      </Stack>

      <Stack direction={'column'} gap={5} className={cx('viewAction')}>
        <Grid2 columnSpacing={'24px'} alignItems={'start'} rowSpacing={'24px'} container>
          <Grid2 size={3}>
            <BaseDatePicker
              mode='date'
              height={40}
              label={t('common_text_start_date', { ns: 'common/base' })}
              value={paramsObject.startDate}
              onChange={(date) => {
                setParamsObject((prevState) => ({
                  ...prevState,
                  startDate: date as string,
                }));
              }}
            />
          </Grid2>

          <Grid2 size={3}>
            <BaseDatePicker
              mode='date'
              height={40}
              label={t('common_text_end_date', { ns: 'common/base' })}
              value={paramsObject.endDate}
              onChange={(date) => {
                setParamsObject((prevState) => ({
                  ...prevState,
                  endDate: date as string,
                }));
              }}
            />
          </Grid2>
        </Grid2>

        <Stack direction='row' justifyContent={'space-between'} alignItems='center'>
          <Button
            type='button'
            variant='contained'
            className={cx('buttonApply')}
            onClick={handleApplyFilter}
            startIcon={<img src={icons.commonIconFilter} alt={t('common_alt_img', { ns: 'common/base' })} />}
          >
            {t('common_action_apply', { ns: 'common/base' })}
          </Button>

          <Button
            type='button'
            variant='contained'
            className={cx('buttonExport')}
            onClick={handleExport}
            disabled={!quarantineList.length}
            startIcon={<img src={icons.commonIconExportExcel} alt={t('common_alt_img', { ns: 'common/base' })} />}
          >
            {t('common_button_export_excel', { ns: 'common/base' })}
          </Button>
        </Stack>
      </Stack>

      <Box className={cx('tableContainer')}>
        <BaseTable
          dataSource={quarantineList}
          currentPage={pageSelected}
          totalPage={pagination?.totalPages}
          columns={columns(t)}
          onPageChange={handlePageChange}
        />
      </Box>

      {isShowUnderDevelopment && <UnderDevelopment onClose={handleShowUnderDevelopment} />}

      {isLoading && <Loading />}
    </Stack>
  );
};

export default QuarantineReport;
